/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { createContext, useContext } from 'react';
import { Platform } from 'react-native';
import type { SearchFormState } from '../types';
import type { SnapChangeFlightStep } from '../types/flightListing';

export type SnapNavigationFilters = {
  guestRating?: string;
  selectedLandmark?: string;
  selectedPropertyTypes?: string[];
  starRating?: number[];
};

export type SnapNavigationOptions = {
  filters?: SnapNavigationFilters;
};

export interface SnapNavigationContextType {
  navigateToResultPage: (
    searchParams: SearchFormState,
    options?: SnapNavigationOptions,
  ) => void;
  replaceResultPageSearchParams?: (
    searchParams: SearchFormState,
    options?: SnapNavigationOptions,
  ) => void;
  navigateToHotelsList: (queryString: string) => void;
  navigateBack: () => void;
  handleRedirection: (redirectionUrl: string) => void;
  /**
   * Change-flight flow: departing list → returning list → hotel listing.
   * `queryString` is the full query for the target page. Optional until
   * every host wires it; without it Change Flight does nothing.
   */
  navigateToChangeFlightStep?: (
    step: SnapChangeFlightStep,
    queryString: string,
  ) => void;
  /** Optional until hosts wire it; callers fall back to navigateBack. */
  navigateToSearch?: () => void;
  searchParams?: Record<string, string | string[] | boolean | undefined>;
}

export const SnapNavigationContext = createContext<
  SnapNavigationContextType | undefined
>(undefined);

export const useSnapNavigation = (): SnapNavigationContextType => {
  const ctx = useContext(SnapNavigationContext);
  if (!ctx) {
    // Return a robust standalone fallback for test environments or legacy mobile setups
    let fallbackParams = {};
    const isTestEnv =
      typeof jest !== 'undefined' || process.env.NODE_ENV === 'test';
    if (isTestEnv || Platform.OS !== 'web') {
      try {
        const { useLocalSearchParams } = require('expo-router');
        fallbackParams = useLocalSearchParams();
      } catch (e) {
        // Fallback silently if expo-router is not available
      }
    }
    return {
      navigateToResultPage: () => {},
      navigateToHotelsList: () => {},
      navigateBack: () => {},
      handleRedirection: () => {},
      searchParams: fallbackParams,
    };
  }
  return ctx;
};
